export const API_PATHS = {
  rfp: {
    /** Usage metering of the backends (ticket 2026-10-18-usage-limits-and-traffic); `ryd` only. */
    usage: {
      overview: `/usage/overview`,
      /** Usage & Limits of the caller's company (ryd staff: `?company=`). */
      summary: `/usage/summary`,
      /** Documented limits per plan and the enforcement profile; any signed-in user. */
      plans: `/usage/plans`,
      /** Per-company limit overrides; ryd staff only. */
      overrides: (company: string) => `/usage/overrides/${encodeURIComponent(company)}`,
    },
    /** OpenAPI documentation hub (rp-users `modules/api-docs-hub`, topic 2026-10-openapi-documentation). */
    apiDocs: {
      catalog: `/api-docs/catalog`,
      roles: `/api-docs/roles`,
      services: `/api-docs/services`,
      validate: `/api-docs/services/validate`,
      adminServices: `/api-docs/admin/services`,
      service: (id: string) => `/api-docs/services/${encodeURIComponent(id)}`,
      archiveService: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/archive`,
      restoreService: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/restore`,
      spec: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/spec`,
      refresh: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/refresh`,
      sync: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/sync`,
      access: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/access`,
      serviceGrants: (id: string) => `/api-docs/services/${encodeURIComponent(id)}/access-grants`,
      revokeGrant: (id: string, uid: string, role: string) =>
        `/api-docs/services/${encodeURIComponent(id)}/access-grants/${encodeURIComponent(uid)}/${encodeURIComponent(role)}`,
      userGrants: (uid: string) => `/api-docs/users/${encodeURIComponent(uid)}/access-grants`,
      discovery: `/api-docs/discovery/gitlab`,
      importFromGitLab: `/api-docs/discovery/gitlab/import`,
      discoveryProject: (projectId: number) => `/api-docs/discovery/gitlab/projects/${projectId}`,
      auditPending: `/api-docs/audit/pending`,
      auditReplay: `/api-docs/audit/replay`,
      /** Try-out audit log (admin): page of calls, and one call with its request and response. */
      tryOutCalls: `/api-docs/audit/calls`,
      tryOutCall: (eventId: string) => `/api-docs/audit/calls/${encodeURIComponent(eventId)}`,
      /** Prefix the viewer rewrites try-out calls onto. */
      proxyBase: (id: string, env: string) =>
        `/api-docs/proxy/${encodeURIComponent(id)}/${encodeURIComponent(env)}`,
    },
    auth: {
      me: `/auth/me`,
      sendResetEmail: `/auth/send-reset-email`,
      validateToken: `/auth/token-validation`,
      validateTokenAndSetPassword: `/auth/validate-token-and-set-password`,
      ssoLogin: `/auth/sso/login`,
      otp: {
        send: `/auth/otp/send`,
        verify: `/auth/otp/verify`,
      },
      twoFactor: {
        status: `/auth/2fa/status`,
        enable: `/auth/2fa/enable`,
        disable: `/auth/2fa/disable`,
        history: `/auth/2fa/history`,
        markEmailVerified: `/auth/2fa/mark-email-verified`,
        backupCodes: {
          verify: `/auth/2fa/backup-codes/verify`,
          regenerate: `/auth/2fa/backup-codes/regenerate`,
        },
      },
      webauthn: {
        register: {
          begin: `/auth/webauthn/register/begin`,
          complete: `/auth/webauthn/register/complete`,
        },
        authenticate: {
          begin: `/auth/webauthn/authenticate/begin`,
          complete: `/auth/webauthn/authenticate/complete`,
        },
        credentials: `/auth/webauthn/credentials`,
      },
      /** AI assistant connection consent (ryd-portal-mcp, D-29): challenge, second factor, assertion. */
      mcpConsent: {
        challenge: `/auth/mcp-consent/challenge`,
        verify: `/auth/mcp-consent/challenge/verify`,
        assertion: `/auth/mcp-consent-assertion`,
      },
    },
    users: {
      list: `/users`,
      emailAvailability: `/users/email-availability`,
      byId: (uid: string) => `/users/${uid}`,
      /** Pending SSO list rows use uid `pending-sso:<docId>`. */
      resendSsoInvitation: (uid: string) =>
        `/users/${encodeURIComponent(uid)}/resend-sso-invitation`,
      enable: (uid: string) => `/users/${uid}/enable`,
      disable: (uid: string) => `/users/${uid}/disable`,
      /** Revokes the user's refresh tokens so they must sign in again (e.g. after a role change). */
      revoke: (uid: string) => `/users/${uid}/revoke`,
      disable2fa: (uid: string) => `/users/${uid}/2fa`,
      twoFactorHistory: (uid: string) => `/users/${uid}/2fa/history`,
      ryd: {
        all: `/users/ryd/all`,
      },
      /** Ryd-only: simplified user list (uid, name, email) of one partner. */
      byUserGroup: (userGroup: string) => `/users/by-user-group/${encodeURIComponent(userGroup)}`,
      keyAccountManagers: `/key-account-managers`,
      assignKeyAccountManager: (uid: string) => `/users/${uid}/assign-key-account-manager`,
    },
    user: {
      availableUserGroups: `/user/available-user-groups`,
      switchUserGroup: `/user/switch-user-group`,
    },
    userBookmarks: {
      list: `/user-bookmarks`,
      byId: (id: string) => `/user-bookmarks/${id}`,
      reorder: `/user-bookmarks/reorder`,
      access: (id: string) => `/user-bookmarks/${id}/access`,
    },
    userSettings: {
      all: `/user-settings`,
      group: (group: string) => `/user-settings/${group}`,
      key: (group: string, key: string) => `/user-settings/${group}/${encodeURIComponent(key)}`,
    },
    settings: {
      enums: `/settings/enums`,
    },
    partnerOnboarding: {
      previewUserGroup: `/partner-onboarding/preview-user-group`,
      journeys: `/partner-onboarding/journeys`,
      journeyById: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}`,
      runStep: (journeyId: string, stepName: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/steps/${encodeURIComponent(stepName)}/run`,
      skipStep: (journeyId: string, stepName: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/steps/${encodeURIComponent(stepName)}/skip`,
      completeStepManually: (journeyId: string, stepName: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/steps/${encodeURIComponent(stepName)}/complete-manual`,
      acknowledgeManual: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/acknowledge-manual`,
      saveHints: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/save-hints`,
      enableRydpay: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/enable-rydpay`,
      events: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/events`,
      eventBody: (journeyId: string, eventId: string, which: 'request' | 'response') =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/events/${encodeURIComponent(
          eventId,
        )}/body/${which}`,
      postpayPreview: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/postpay-preview`,
      sponsorByUserGroup: `/partner-onboarding/sponsor-by-user-group`,
      comments: (journeyId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/comments`,
      commentById: (journeyId: string, commentId: string) =>
        `/partner-onboarding/journeys/${encodeURIComponent(journeyId)}/comments/${encodeURIComponent(commentId)}`,
      checklists: `/partner-onboarding/checklists`,
      checklistById: (checklistId: string) =>
        `/partner-onboarding/checklists/${encodeURIComponent(checklistId)}`,
      checklistCustomItems: (checklistId: string) =>
        `/partner-onboarding/checklists/${encodeURIComponent(checklistId)}/custom-items`,
      checklistCustomItemById: (checklistId: string, itemId: string) =>
        `/partner-onboarding/checklists/${encodeURIComponent(checklistId)}/custom-items/${encodeURIComponent(itemId)}`,
    },
    partnerConfigs: {
      list: `/partner-configs`,
      byId: (id: string | number) => `/partner-configs/${id}`,
      byNames: `/partner-configs/by-names`,
      featureFlags: (id: string | number) => `/partner-configs/${id}/feature-flags`,
      featureFlagsByName: (partnerName: string) =>
        `/partner-configs/by-name/${encodeURIComponent(partnerName)}/feature-flags`,
      onboardingConfigByName: (partnerName: string) =>
        `/partner-configs/by-name/${encodeURIComponent(partnerName)}/onboarding-config`,
      auditLogs: (partnerName: string) =>
        `/partner-configs/by-name/${encodeURIComponent(partnerName)}/audit-logs`,
      auditLogBody: (partnerName: string, eventId: string, which: string) =>
        `/partner-configs/by-name/${encodeURIComponent(partnerName)}/audit-logs/${encodeURIComponent(eventId)}/body/${which}`,
      user: `/partner-configs/user`,
      fleetSubscriptionPlan: (partnerName: string) =>
        `/partner-configs/subscription-plan/${encodeURIComponent(partnerName)}`,
      marketingData: (partnerName: string) =>
        `/partner-configs/marketing-data/${encodeURIComponent(partnerName)}`,
      egressAllowlist: (partnerName: string) =>
        `/partner-configs/egress-allowlist/${encodeURIComponent(partnerName)}`,
      inboundAllowlist: (partnerName: string) =>
        `/partner-configs/inbound-allowlist/${encodeURIComponent(partnerName)}`,
      alertSlackChannels: (id: string) => `/partner-configs/${id}/alert-slack-channels`,
      alertSlackChannel: (id: string, channelId: string) =>
        `/partner-configs/${id}/alert-slack-channels/${channelId}`,
      alertSlackChannelDisable: (id: string, channelId: string) =>
        `/partner-configs/${id}/alert-slack-channels/${channelId}/disable`,
      alertSlackChannelTest: (id: string, channelId: string) =>
        `/partner-configs/${id}/alert-slack-channels/${channelId}/test`,
    },
    partnerUiSettings: {
      list: `/partner/ui-settings`,
      partner: `/partner/ui-settings/partner`,
      byId: (id: string | number) => `/partner/ui-settings/${id}`,
      reactivate: (id: string | number) => `/partner/ui-settings/${id}/reactivate`,
      importCss: (id: string | number) => `/partner/ui-settings/${id}/import-css`,
    },
    fleetCredentials: {
      client: {
        get: `/backend-fleet-service-proxy/credentials/client`,
        generate: `/backend-fleet-service-proxy/credentials/client/generate`,
        scopes: `/backend-fleet-service-proxy/credentials/client/scopes`,
      },
      partner: `/backend-fleet-service-proxy/credentials/partner`,
    },
    partnerDataValidation: {
      base: `/backend-fleet-service-proxy/partner-data-validation`,
    },
    partnerFraudRules: {
      base: `/backend-fleet-service-proxy/partner-fraud-rules`,
      byId: (id: string) =>
        `/backend-fleet-service-proxy/partner-fraud-rules/${encodeURIComponent(id)}`,
      validate: `/backend-fleet-service-proxy/partner-fraud-rules/validate`,
    },
    partnerFleetConfigAudit: {
      list: `/backend-fleet-service-proxy/partner-fleet-config-audit-logs`,
      body: (eventId: string | number, which: 'request' | 'response') =>
        `/backend-fleet-service-proxy/partner-fleet-config-audit-logs/${encodeURIComponent(String(eventId))}/body/${which}`,
    },
    fleetManagement: {
      drivers: {
        list: `/fleet-management/drivers`,
        byId: (accountId: string) => `/fleet-management/drivers/${accountId}`,
        dropdown: `/fleet-management/drivers/dropdown`,
        /** Thin, paginated, searchable rows for the driver pickers — no onboarding or limit enrichment. */
        options: `/fleet-management/drivers/options`,
        limits: `/fleet-management/drivers/limits`,
        upload: `/fleet-management/drivers/upload`,
      },
      odometer: {
        bySponsorId: (sponsorId: string) => `/fleet-management/odometer/${sponsorId}`,
        settings: `/fleet-management/odometer`,
      },
      driverSettings: `/fleet-management/driver-settings`,
      /** rp-users resolves the fleet from the auth token — never send a fleetId from here. */
      vehicles: {
        list: `/fleet-management/vehicles`,
        create: `/fleet-management/vehicles`,
        byId: (thingId: string) => `/fleet-management/vehicles/${encodeURIComponent(thingId)}`,
        /** Unimplemented by rp-users; kept so `admin-tools/FleetVehicles.vue` and `FleetReports.vue` resolve. */
        dropdown: `/fleet-management/vehicles/dropdown`,
        status: (id: string) => `/fleet-management/vehicles/${id}/status`,
        transactions: (id: string) => `/fleet-management/vehicles/${id}/transactions`,
      },
      vehicleCatalog: {
        makes: `/fleet-management/vehicle-catalog/makes`,
        models: (makeId: string) =>
          `/fleet-management/vehicle-catalog/makes/${encodeURIComponent(makeId)}/models`,
      },
      payment: `/fleet-management/payment`,
      csvUpload: {
        drivers: `/fleet-management/csv-upload/drivers`,
        sessions: `/fleet-management/csv-upload/sessions`,
        status: (sessionId: string) => `/fleet-management/csv-upload/status/${sessionId}`,
      },
      limits: '/fleet-management/limits',
      settings: '/fleet-management/settings',
      fraudSettings: '/fleet-management/fraud-settings',
      paymentSettings: '/fleet-management/payment-settings',
      reports: {
        list: `/fleet-management/reports`,
        byId: (id: string) => `/fleet-management/reports/${id}`,
        download: (id: string) => `/fleet-management/reports/${id}/download`,
        generate: `/fleet-management/reports/generate`,
      },
      analytics: '/fleet-management/analytics',
      // old endpoint to take data from data warehouse (no nfc data included)
      // transactions: '/fleet-management/transactions',
      transactions: '/fleet-management/v2/transactions',
      ccRecipients: '/fleet-management/cc-recipients',
      /** Single source of truth for the Home page: onboarding stepper state + post-setup summary. */
      home: {
        get: `/fleet-management/home`,
        markStep: (stepName: string) =>
          `/fleet-management/home/onboarding/steps/${encodeURIComponent(stepName)}`,
        paymentChoice: `/fleet-management/home/onboarding/payment-choice`,
        // TEMP (dev/QA only, TASKFORCE-2748): remove alongside the endpoint once testing is done.
        resetOnboarding: `/fleet-management/home/onboarding/reset`,
      },
    },
    payByInvoice: {
      journey: `/pay-by-invoice/journey`,
      start: `/pay-by-invoice/journey/start`,
      calculator: `/pay-by-invoice/calculator`,
      step: (stepName: string) => `/pay-by-invoice/journey/steps/${encodeURIComponent(stepName)}`,
      submitStep: (stepName: string) =>
        `/pay-by-invoice/journey/steps/${encodeURIComponent(stepName)}/submit`,
      forwardTransferDetails: `/pay-by-invoice/journey/transfer-details/forward`,
      tncCurrent: `/pay-by-invoice/tnc/current`,
    },
    fleetOperations: {
      orders: {
        bySponsorId: '/fleet-operations/orders/by-sponsor-id',
      },
      partnerBillingConfiguration: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/billing-configuration`,
      partnerPeriodSpending: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/period-spending`,
      partnerBillingSchedulePreview: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/billing-schedule-preview`,
      partnerInvoices: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/invoices`,
      partnerInvoiceAttachment: (sponsorId: string, attachmentId: string | number) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/invoices/attachments/${encodeURIComponent(String(attachmentId))}`,
      partnerPreferredCarCheck: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/preferred-car-check`,
      partnerNfcReadinessCheck: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/nfc-readiness-check`,
      bmwCoreSponsor: `/fleet-operations/bmw/core-sponsor`,
      bmwHealthCheckDefinition: `/fleet-operations/bmw/health-check/definition`,
      partnerBmwReadiness: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/bmw-readiness`,
      partnerBmwDeltaSponsorCreate: (sponsorId: string, deltaId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/bmw-readiness/delta-sponsors/${encodeURIComponent(deltaId)}`,
      partnerVehicleUsage: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/vehicle-usage`,
      partnerVehicleAssignmentHistory: (sponsorId: string, thingId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/vehicles/${encodeURIComponent(thingId)}/assignments`,
      partnerCarPoolStatus: '/fleet-operations/partners/car-pool-status',
      partnerCarPoolIndex: '/fleet-operations/partners/car-pool-index',
      partnerCarPoolIndexRefresh: '/fleet-operations/partners/car-pool-index/refresh',
      partnerVehicleAssignmentLog: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/vehicle-assignment-log`,
      partnerVehicleAuditEvents: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/vehicle-audit-events`,
      partnerVehicleActivity: (sponsorId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/vehicle-activity`,
      partnerProvisionVirtualCard: (sponsorId: string, accountId: string) =>
        `/fleet-operations/partners/${encodeURIComponent(sponsorId)}/accounts/${encodeURIComponent(accountId)}/virtual-card`,
      driverCreationAudit: {
        events: '/fleet-operations/driver-creation/events',
        eventNames: '/fleet-operations/driver-creation/event-names',
      },
      paymentTransitions: {
        list: (fleetId: string) =>
          `/fleet-operations/fleets/${encodeURIComponent(fleetId)}/payment-transitions`,
        create: (fleetId: string) =>
          `/fleet-operations/fleets/${encodeURIComponent(fleetId)}/payment-transitions`,
        auditLogs: `/fleet-operations/payment-transitions/audit-logs`,
      },
      payByInvoice: {
        journeys: `/fleet-operations/pay-by-invoice/journeys`,
        journey: (journeyId: string) =>
          `/fleet-operations/pay-by-invoice/journeys/${encodeURIComponent(journeyId)}`,
        confirm: (journeyId: string) =>
          `/fleet-operations/pay-by-invoice/journeys/${encodeURIComponent(journeyId)}/confirm`,
        reject: (journeyId: string) =>
          `/fleet-operations/pay-by-invoice/journeys/${encodeURIComponent(journeyId)}/reject`,
        reset: (journeyId: string) =>
          `/fleet-operations/pay-by-invoice/journeys/${encodeURIComponent(journeyId)}/reset`,
        events: (journeyId: string) =>
          `/fleet-operations/pay-by-invoice/journeys/${encodeURIComponent(journeyId)}/events`,
        eventBody: (journeyId: string, eventId: string, which: string) =>
          `/fleet-operations/pay-by-invoice/journeys/${encodeURIComponent(journeyId)}/events/${encodeURIComponent(eventId)}/body/${encodeURIComponent(which)}`,
      },
    },
    fleetInvoices: {
      list: `/fleet-invoices`,
      invoiceNumbersAutocomplete: `/fleet-invoices/invoice-numbers/autocomplete`,
      attachments: (attachmentId: string) => `/fleet-invoices/attachments/${attachmentId}`,
    },
    fleetInvoicesV3: {
      list: `/v3/fleet-invoices`,
      invoiceNumbersAutocomplete: `/v3/fleet-invoices/invoice-numbers/autocomplete`,
      attachments: (attachmentId: string) => `/v3/fleet-invoices/attachments/${attachmentId}`,
      byOrder: (orderUuid: string) =>
        `/v3/fleet-invoices/by-order/${encodeURIComponent(orderUuid)}`,
    },
    invoiceMirrorAdmin: {
      status: `/invoice-mirror-admin/status`,
      invoices: `/invoice-mirror-admin/invoices`,
      run: `/invoice-mirror-admin/run`,
    },
    fiscalMonitoring: {
      freshness: `/fiscal-monitoring/freshness`,
      poland: {
        /** Merged Odoo requests + webhook callbacks, time-ordered. */
        events: `/fiscal-monitoring/poland/events`,
        outbound: `/fiscal-monitoring/poland/outbound`,
        outboundDetail: (odooId: number | string) => `/fiscal-monitoring/poland/outbound/${odooId}`,
        inbound: `/fiscal-monitoring/poland/inbound`,
        inboundDetail: (eventId: string) => `/fiscal-monitoring/poland/inbound/${eventId}`,
        orderTimeline: (orderUuid: string) =>
          `/fiscal-monitoring/poland/order/${encodeURIComponent(orderUuid)}/timeline`,
        resendDraft: (odooId: number | string) =>
          `/fiscal-monitoring/poland/outbound/${odooId}/resend-draft`,
        resendValidate: `/fiscal-monitoring/poland/resend/validate`,
        resendExecute: `/fiscal-monitoring/poland/resend`,
        /** Who resent which body for one order — write-ahead intent docs, newest first. */
        resendAudit: (orderUuid: string) =>
          `/fiscal-monitoring/poland/order/${encodeURIComponent(orderUuid)}/resend-audit`,
      },
      moloni: {
        methods: `/fiscal-monitoring/moloni/methods`,
        operations: `/fiscal-monitoring/moloni/operations`,
        operationDetail: (odooId: number | string) =>
          `/fiscal-monitoring/moloni/operations/${odooId}`,
        orderOperation: (orderUuid: string) =>
          `/fiscal-monitoring/moloni/order/${encodeURIComponent(orderUuid)}/operation`,
        logs: `/fiscal-monitoring/moloni/logs`,
        logDetail: (odooId: number | string) => `/fiscal-monitoring/moloni/logs/${odooId}`,
        /** Raw call -> the reconstructed operation it belongs to. */
        logOperation: (odooId: number | string) =>
          `/fiscal-monitoring/moloni/logs/${odooId}/operation`,
      },
    },
    fleetExports: {
      create: `/fleet-exports`,
      list: `/fleet-exports`,
      detail: (jobId: string) => `/fleet-exports/${jobId}`,
      download: (jobId: string) => `/fleet-exports/${jobId}/download`,
    },
    fleetExportsV3: {
      create: `/v3/fleet-exports`,
      list: `/v3/fleet-exports`,
      detail: (jobId: string) => `/v3/fleet-exports/${jobId}`,
      download: (jobId: string) => `/v3/fleet-exports/${jobId}/download`,
    },
    partnerExports: {
      create: `/partner-exports`,
      list: `/partner-exports`,
      detail: (jobId: string) => `/partner-exports/${jobId}`,
      download: (jobId: string) => `/partner-exports/${jobId}/download`,
    },
    fleetOperationsCapability: `/me/fleet-operations-capability`,
    onboardingPrechecks: `/me/onboarding-prechecks`,
    onboardingPrechecksForPartner: (userGroup: string) =>
      `/partners/${encodeURIComponent(userGroup)}/onboarding-prechecks`,
    rydpayOnboardingStatus: `/me/rydpay-onboarding-status`,
    rydpayOnboardingStatusForPartner: (userGroup: string) =>
      `/partners/${encodeURIComponent(userGroup)}/rydpay-onboarding-status`,
    partnerFleetOperations: {
      subFleets: `/partner-fleet-operations/sub-fleets`,
      subFleet: (fleetId: string) => `/partner-fleet-operations/sub-fleets/${fleetId}`,
      subFleetDrivers: (fleetId: string) =>
        `/partner-fleet-operations/sub-fleets/${fleetId}/drivers`,
    },
    fleetPartnerSuspension: {
      suspend: (sponsorId: string) =>
        `/fleet-partner-suspension/${encodeURIComponent(sponsorId)}/suspend`,
      unsuspend: (sponsorId: string) =>
        `/fleet-partner-suspension/${encodeURIComponent(sponsorId)}/unsuspend`,
      recipients: (sponsorId: string) =>
        `/fleet-partner-suspension/${encodeURIComponent(sponsorId)}/recipients`,
      precheck: (sponsorId: string) =>
        `/fleet-partner-suspension/${encodeURIComponent(sponsorId)}/precheck`,
      emails: (sponsorId: string) =>
        `/fleet-partner-suspension/${encodeURIComponent(sponsorId)}/emails`,
      email: (sponsorId: string, emailId: string) =>
        `/fleet-partner-suspension/${encodeURIComponent(sponsorId)}/emails/${encodeURIComponent(emailId)}`,
      auditTrail: `/fleet-partner-suspension/audit-trail`,
      journeys: `/fleet-partner-suspension/journeys`,
      journey: (journeyId: string) =>
        `/fleet-partner-suspension/journeys/${encodeURIComponent(journeyId)}`,
      resumeJourney: (journeyId: string) =>
        `/fleet-partner-suspension/journeys/${encodeURIComponent(journeyId)}/resume`,
    },
    batchProcessing: {
      sponsorSearch: `/batch-processing/sponsor-search`,
      fleetSearch: `/batch-processing/fleet-search`,
      runs: `/batch-processing/runs`,
      run: (runId: string) => `/batch-processing/runs/${encodeURIComponent(runId)}`,
      execute: (runId: string) => `/batch-processing/runs/${encodeURIComponent(runId)}/execute`,
      retryItem: (runId: string, itemId: string) =>
        `/batch-processing/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/retry`,
      skipItem: (runId: string, itemId: string) =>
        `/batch-processing/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/skip`,
      verifyItem: (runId: string, itemId: string) =>
        `/batch-processing/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/verify`,
      stepBody: (runId: string, itemId: string, stepName: string, which: 'request' | 'response') =>
        `/batch-processing/runs/${encodeURIComponent(runId)}/items/${encodeURIComponent(itemId)}/steps/${encodeURIComponent(stepName)}/body/${which}`,
    },
    mailchimpAudienceExport: {
      schedulers: `/mailchimp-audience-export/schedulers`,
      scheduler: (schedulerKey: string) =>
        `/mailchimp-audience-export/schedulers/${encodeURIComponent(schedulerKey)}`,
      trigger: (schedulerKey: string) =>
        `/mailchimp-audience-export/schedulers/${encodeURIComponent(schedulerKey)}/trigger`,
      records: `/mailchimp-audience-export/records`,
      record: (recordKey: string) =>
        `/mailchimp-audience-export/records/${encodeURIComponent(recordKey)}`,
      review: `/mailchimp-audience-export/records/review`,
      import: `/mailchimp-audience-export/import`,
    },
    partnerBilling: {
      partners: `/partner-billing/partners`,
      partner: (sponsorId: string) => `/partner-billing/partners/${encodeURIComponent(sponsorId)}`,
      partnerDrivers: (sponsorId: string) =>
        `/partner-billing/partners/${encodeURIComponent(sponsorId)}/drivers`,
      partnerRefreshLimits: (sponsorId: string) =>
        `/partner-billing/partners/${encodeURIComponent(sponsorId)}/refresh-limits`,
      partnerRefresh: (sponsorId: string) =>
        `/partner-billing/partners/${encodeURIComponent(sponsorId)}/refresh`,
      refreshJobLatest: (sponsorId: string) =>
        `/partner-billing/partners/${encodeURIComponent(sponsorId)}/refresh-jobs/latest`,
      refreshJob: (jobId: string) => `/partner-billing/refresh-jobs/${encodeURIComponent(jobId)}`,
      analyticsSummary: `/partner-billing/analytics/summary`,
      analyticsCycleClosings: `/partner-billing/analytics/cycle-closings`,
      analyticsHistory: `/partner-billing/analytics/history`,
    },
    invoices: {
      search: `/invoices/search`,
    },
    featureConfigurations: {
      list: `/feature-configurations`,
      byId: (id: string) => `/feature-configurations/${id}`,
      sync: `/feature-configurations/sync`,
      auditLogs: (id: string) => `/feature-configurations/${encodeURIComponent(id)}/audit-logs`,
      auditLogBody: (id: string, eventId: string, which: string) =>
        `/feature-configurations/${encodeURIComponent(id)}/audit-logs/${encodeURIComponent(eventId)}/body/${which}`,
    },
    webhook: {
      configurationsPartners: `/backend-webhook-gateway-proxy/webhook-configurations/partners`,
      configurationPartner: (id: string | number) =>
        `/backend-webhook-gateway-proxy/webhook-configurations/partners/${encodeURIComponent(String(id))}`,
      configurationEndpoints: `/backend-webhook-gateway-proxy/webhook-configurations/endpoints`,
      configurationEndpoint: (id: string | number) =>
        `/backend-webhook-gateway-proxy/webhook-configurations/endpoints/${encodeURIComponent(String(id))}`,
      auditLogs: `/backend-webhook-gateway-proxy/webhook-configurations/audit-logs`,
      auditLogBody: (eventId: string | number, which: 'request' | 'response') =>
        `/backend-webhook-gateway-proxy/webhook-configurations/audit-logs/${encodeURIComponent(String(eventId))}/body/${which}`,
      testSend: `/backend-webhook-gateway-proxy/webhook-configurations/test-send`,
    },
    pcsSales: {
      target: `/backend-pcs-sales-proxy/target`,
      stationMappings: `/backend-pcs-sales-proxy/station-mappings`,
      stationMapping: (poiId: string) =>
        `/backend-pcs-sales-proxy/station-mappings/${encodeURIComponent(poiId)}`,
      stationMappingMode: (poiId: string) =>
        `/backend-pcs-sales-proxy/station-mappings/${encodeURIComponent(poiId)}/mode`,
      registrations: `/backend-pcs-sales-proxy/registrations`,
      registrationRequeue: (externalId: string) =>
        `/backend-pcs-sales-proxy/registrations/${encodeURIComponent(externalId)}/requeue`,
    },
    fleetCards: {
      list: `/fleet-cards`,
      byIdStatus: (id: string) => `/fleet-cards/${id}/status`,
    },
    sponsors: {
      list: `/sponsors`,
      byId: (id: string) => `/sponsors/${id}`,
      sync: `/sponsors/sync`,
      fleetAutocomplete: `/sponsors/fleet/autocomplete`,
      autocomplete: `/sponsors/autocomplete`,
      voucherDiscountInfo: (sponsorId: string) => `/sponsors/${sponsorId}/voucher-discount-info`,
      removeFromAccount: (sponsorId: string, accountId: string) =>
        `/sponsors/${sponsorId}/accounts/${accountId}`,
      attachToAccount: (sponsorId: string, accountId: string) =>
        `/sponsors/${sponsorId}/accounts/${accountId}`,
    },
    backendUserServiceProxy: {
      features: {
        distinctNames: `/backend-user-service-proxy/features/distinct-names`,
      },
      featureConfigurations: {
        list: `/backend-user-service-proxy/feature-configurations`,
        byId: (id: string) =>
          `/backend-user-service-proxy/feature-configurations/${encodeURIComponent(id)}`,
      },
      /**
       * Partner Filter Rules — station visibility policies and the
       * restricted-entity registry. rp-users owns auth, the per-operation
       * capability check, payload validation and mutation audit logging; the
       * browser never calls user-service directly.
       */
      partnerFilterRules: {
        policies: `/backend-user-service-proxy/partner-filter-policies`,
        policyById: (id: string) =>
          `/backend-user-service-proxy/partner-filter-policies/${encodeURIComponent(id)}`,
        /** Returns the policies that apply to a client — not a per-shop decision. */
        resolve: `/backend-user-service-proxy/partner-filter-policies/resolve`,
        rules: (policyId: string) =>
          `/backend-user-service-proxy/partner-filter-policies/${encodeURIComponent(policyId)}/rules`,
        ruleById: (policyId: string, ruleId: string) =>
          `/backend-user-service-proxy/partner-filter-policies/${encodeURIComponent(policyId)}/rules/${encodeURIComponent(ruleId)}`,
        restrictedEntities: `/backend-user-service-proxy/restricted-entities`,
        restrictedEntityById: (id: string) =>
          `/backend-user-service-proxy/restricted-entities/${encodeURIComponent(id)}`,
        templates: `/backend-user-service-proxy/partner-filter-templates`,
        applyTemplate: (templateId: string) =>
          `/backend-user-service-proxy/partner-filter-templates/${encodeURIComponent(templateId)}/apply`,
        /**
         * Mutation audit trail. Every create, edit, delete and template apply is
         * written on success AND failure, so a missing entry means the request
         * never reached rp-users.
         */
        auditLogs: `/backend-user-service-proxy/partner-filter-audit-logs`,
        auditLogBody: (eventId: string, which: 'request' | 'response') =>
          `/backend-user-service-proxy/partner-filter-audit-logs/${encodeURIComponent(eventId)}/body/${which}`,
      },
      /**
       * Partner Pricing lookup — what an API partner customer is actually
       * served by the List Pricing Engine and the API Partner Discount Engine.
       * user-service has no customer filter on these feeds, so rp-users fans
       * out over every upstream page, filters by `customerId` and/or
       * `partnerIdentifier`, and re-paginates (`limit`/`offset`). Read-only.
       */
      partnerPricing: {
        listPrices: `/backend-user-service-proxy/partner-pricing/list-prices`,
        discounts: `/backend-user-service-proxy/partner-pricing/discounts`,
      },
    },
    backendLegacyProxy: {
      sponsors: {
        list: `/backend-legacy-proxy/sponsors`,
        byId: (id: string) => `/backend-legacy-proxy/sponsors/${id}`,
        auditLogs: (id: string) =>
          `/backend-legacy-proxy/sponsors/${encodeURIComponent(id)}/audit-logs`,
        auditLogBody: (id: string, eventId: string, which: string) =>
          `/backend-legacy-proxy/sponsors/${encodeURIComponent(id)}/audit-logs/${encodeURIComponent(eventId)}/body/${which}`,
      },
    },
    backendLegacySf3: {
      sponsors: {
        getAccountsBySponsorId: `/backend-legacy-sf3/sponsors/get-accounts-by-sponsor-id`,
        getAccountsAcrossSubFleets: `/backend-legacy-sf3/sponsors/get-accounts-across-sub-fleets`,
      },
    },
    backendLegacyUsers: {
      byId: (userId: string) => `/backend-legacy-users/${userId}`,
      tags: (userId: string) => `/backend-legacy-users/${userId}/tags`,
      search: `/backend-legacy-users/search`,
    },
    backendUsers: {
      list: `/backend-users`,
      byId: (userId: string) => `/backend-users/${userId}`,
      searchByEmail: `/backend-users/search/by-email`,
      block: (userId: string) => `/backend-users/${userId}/block`,
      unblock: (userId: string) => `/backend-users/${userId}/unblock`,
      customerSupport: {
        accountById: (accountId: string) => `/backend-users/customer-support/accounts/${accountId}`,
        resolve: (id: string) => `/backend-users/customer-support/resolve/${id}`,
        sponsorsAndTags: (userId: string) =>
          `/backend-users/customer-support/${userId}/sponsors-and-tags`,
        /** Lottery tickets parsed from the user's `*_LOTTERY_*` tags (past draws included). */
        lottery: (userId: string) =>
          `/backend-users/customer-support/${encodeURIComponent(userId)}/lottery`,
        /** User-service profile PATCH (customer support; not fleet PATCH /backend-users/:id). */
        updateUser: (userId: string) => `/customer-support/backend-users/${userId}`,
        /** Archive (soft-delete) an account. POST triggers SF gateway archive pipeline. */
        archiveAccount: (accountId: string) =>
          `/backend-users/customer-support/${accountId}/archive`,
      },
      customerSupportReferralProgram: {
        participation: (customerId: string) => `/customer-support/referral-program/${customerId}`,
      },
      customerSupportFleetVouchers: {
        byUserId: (userId: string) =>
          `/customer-support/fleet-vouchers/${encodeURIComponent(userId)}`,
      },
      customerSupportCars: {
        /** Update an existing car. PUT proxies to SF3 gateway PUT /cars/:thingId */
        update: (thingId: string) => `/customer-support/cars/${thingId}`,
        /** Archive (invalidate) a car. POST proxies to SF3 gateway POST /cars/:thingId/invalidate */
        invalidate: (thingId: string) => `/customer-support/cars/${thingId}/invalidate`,
        /** Create a new car linked to an account. POST proxies to SF3 gateway POST /accounts/:accountId/cars */
        create: (accountId: string) => `/customer-support/accounts/${accountId}/cars`,
        /** Full activity timeline of one user (audit table, event_sink, user-service records; no Graylog). */
        userActivity: (userId: string) =>
          `/customer-support/users/${encodeURIComponent(userId)}/activity`,
      },
      accounts: {
        byId: (accountId: string) => `/backend-users/accounts/${accountId}`,
      },
      /** Create a driver in one of the partner's own fleets / sub-fleets. */
      createDriver: `/backend-users/drivers`,
    },
    backendAuth: {
      resendPassword: `/backend-auth/resend-password`,
      requestDriverOtp: `/backend-auth/request-driver-otp`,
      deleteUserTokens: `/backend-auth/tokens/delete-user-tokens`,
      tokenStatus: `/backend-auth/token-status`,
      partnerCredentials: `/backend-auth/partner-credentials`,
      voucherUrl: `/backend-auth/voucher-url`,
      accountHistoryPreviewUrl: `/backend-auth/account-history-preview-url`,
    },
    /** CS elevated-access whitelist for the end-user support chat ("agent mode"). */
    csWhitelist: {
      list: `/cs-whitelist`,
      create: `/cs-whitelist`,
      entry: (id: string) => `/cs-whitelist/${encodeURIComponent(id)}`,
      auditLogs: `/cs-whitelist/audit-logs`,
    },
    /** Portal guides (presentation, video, handbook) listed on /users/guide; ryd internal users only. */
    guides: {
      list: `/guides`,
      create: `/guides`,
      update: (id: string) => `/guides/${encodeURIComponent(id)}`,
    },
    /** Diagrams from `ryd-portal-guides`, read from the private bucket; rp-users checks the set's role. */
    portalDiagrams: {
      get: (set: string, name: string) =>
        `/portal-diagrams/${encodeURIComponent(set)}/${encodeURIComponent(name)}`,
    },
    backendPayGateway: {
      campaignParticipations: `/backend-pay-gateway/campaign-participations`,
      accountHistory: `/backend-pay-gateway/account-history`,
      extendedCampaignParticipations: `/backend-pay-gateway/extended-campaign-participations`,
    },
    mailchimpMandrill: {
      emailHistory: `/mailchimp-mandrill/email-history`,
      mandrillMessageContent: `/mailchimp-mandrill/mandrill-message-content`,
      mailchimpCampaignDetails: `/mailchimp-mandrill/mailchimp-campaign-details`,
      mandrillTemplates: `/marketing/mandrill-templates`,
      mandrillTemplatesEmailBodySearch: `/marketing/mandrill-templates/email-body-search`,
      mandrillTemplatesLabels: `/marketing/mandrill-templates/labels`,
      mandrillTemplatesTimeSeries: (slug: string) =>
        `/marketing/mandrill-templates/time-series/${encodeURIComponent(slug)}`,
      mandrillTemplatesSyncCatalog: `/marketing/mandrill-templates/sync-catalog`,
      mandrillTemplatePreview: (slug) =>
        `/marketing/mandrill-templates/preview/${encodeURIComponent(slug)}`,
      mandrillTemplatesRefreshStats: `/marketing/mandrill-templates/refresh-stats`,
      mandrillTemplateRefreshStatsSingle: (slug) =>
        `/marketing/mandrill-templates/${encodeURIComponent(slug)}/refresh-stats`,
      mandrillTemplateRefreshBody: (slug: string) =>
        `/marketing/mandrill-templates/${encodeURIComponent(slug)}/refresh-body`,
      mandrillTemplateTestSend: (slug: string) =>
        `/marketing/mandrill-templates/${encodeURIComponent(slug)}/test-send`,
    },
    /** rp-users → payment-service via gateway (customer support: list/activate/deactivate/set-as-preferred/delete by account customerId). */
    paymentMethods: {
      list: `/payment-methods`,
      byEmail: `/payment-methods/by-email`,
      byId: (paymentMethodId: string) => `/payment-methods/${encodeURIComponent(paymentMethodId)}`,
      deactivate: (paymentMethodId: string) =>
        `/payment-methods/${encodeURIComponent(paymentMethodId)}/deactivate`,
      activate: (paymentMethodId: string) =>
        `/payment-methods/${encodeURIComponent(paymentMethodId)}/activate`,
      setPreferred: (paymentMethodId: string) =>
        `/payment-methods/${encodeURIComponent(paymentMethodId)}/set-as-preferred`,
    },
    campaigns: {
      byId: (id: string) => `/campaigns/${encodeURIComponent(id)}`,
      details: (id: string) => `/campaigns/${encodeURIComponent(id)}/details`,
    },
    vouchers: {
      byCode: (code: string) => `/vouchers/${encodeURIComponent(code)}`,
      details: (code: string) => `/vouchers/${encodeURIComponent(code)}/details`,
      disable: (code: string) => `/vouchers/${encodeURIComponent(code)}/disable`,
      enable: (code: string) => `/vouchers/${encodeURIComponent(code)}/enable`,
      bulkAction: `/vouchers/customer-support/bulk-action`,
    },
    /** BigQuery audit_interactions (rp-users) — customer support read-only */
    customerSupport: {
      /** Grouped partial first/last name search. Returns { both, firstNameOnly, lastNameOnly }. */
      searchByName: `/customer-support/users/search-by-name`,
      auditLogsByUser: `/customer-support/audit-logs/by-user`,
      auditLogsByOrder: `/customer-support/audit-logs/by-order`,
      auditLogsByVoucher: `/customer-support/audit-logs/by-voucher`,
      voucherBuyer: (code: string) =>
        `/customer-support/vouchers/${encodeURIComponent(code)}/buyer`,
      voucherDetails: (code: string) =>
        `/customer-support/vouchers/${encodeURIComponent(code)}/details`,
      /** Goodwill (Kulanz) gift-card issuance — create → fund → publish in rp-users. */
      goodwillVouchers: {
        issue: (accountId: string) =>
          `/customer-support/users/${encodeURIComponent(accountId)}/goodwill-vouchers`,
        /** Count of goodwill vouchers the signed-in actor issued today (soft duplicate warning). */
        issuedTodayCount: `/customer-support/goodwill-vouchers/issued-today-count`,
      },
      userByIdOrAccountId: `/backend-users/customer-support/by-id-or-account-id`,
      invoiceByOrderUuid: `/customer-support/invoice/by-order-uuid`,
      /** Resolve a ryd URL-shortener uuid/URL to its long URL, parse params, and decrypt the `data` blob. */
      deeplinkResolver: `/customer-support/deeplink-resolver`,
      /** Find users by partner + customerIdAtPartner (via uniqueCustomerIdAtPartner). */
      deeplinkResolverUsers: `/customer-support/deeplink-resolver/users`,
      /**
       * Read the chat verification code that was diverted from a customer during the CS test
       * window, so an agent can verify as that customer in the support chat. Only returns a code
       * while interception is active in ryd-end-user-ai; every read is audited on both sides.
       */
      otpIntercept: `/customer-support/otp-intercept`,
      /**
       * Browse the codes currently waiting, by email. Returns summaries only — never a code;
       * revealing one is the separate, audited `otpIntercept` read above.
       */
      otpInterceptList: `/customer-support/otp-intercept/list`,
      /** Live feature-flag state of the support chat AI, per environment. Read-only. */
      chatAiStatus: `/customer-support/chat-ai/status`,
      /** Compare TT4 shop identity/address/coordinates with live Google Places data. */
      googlePlaceShopValidation: `/customer-support/google-places/shop-validation`,
      /** Compare a TT4 shop with TomTom, Open Charge Map and the German charging-station register. */
      stationSourceValidation: `/customer-support/stations/source-validation`,
      /** Customer status reported by external partners (e.g. freenet). */
      customerStatusAtPartner: {
        list: `/customer-support/customer-status-at-partner`,
        byCustomerId: `/customer-support/customer-status-at-partner/by-customer-id`,
        processing: `/customer-support/customer-status-at-partner/processing`,
        processingHistory: `/customer-support/customer-status-at-partner/processing/history`,
      },
      odooAdditionalDataByOrder: (saleOrderId: number | string) =>
        `/customer-support/odoo/sale-orders/${encodeURIComponent(String(saleOrderId))}/additional-data`,
      odooAdditionalDataRow: (rowId: number | string) =>
        `/customer-support/odoo/additional-data/${encodeURIComponent(String(rowId))}`,
      hackerAnalysisUserActivity: `/customer-support/hacker-analysis/user-activity`,
      orderGeolocation: `/customer-support/hacker-analysis/order-geolocation`,
      orderPdfDownloadAudit: `/customer-support/hacker-analysis/order-pdf-download-audit`,
      remoteFueling: `/customer-support/hacker-analysis/remote-fueling`,
      remoteFuelingExport: `/customer-support/hacker-analysis/remote-fueling/export`,
      remoteFuelingScan: `/customer-support/hacker-analysis/remote-fueling/scan`,
      remoteFuelingRecord: (orderId: string) =>
        `/customer-support/hacker-analysis/remote-fueling/records/${encodeURIComponent(orderId)}`,
      remoteFuelingRecordStatus: (orderId: string) =>
        `/customer-support/hacker-analysis/remote-fueling/records/${encodeURIComponent(orderId)}/status`,
      remoteFuelingRecordComments: (orderId: string) =>
        `/customer-support/hacker-analysis/remote-fueling/records/${encodeURIComponent(orderId)}/comments`,
      remoteFuelingBulkStatus: `/customer-support/hacker-analysis/remote-fueling/bulk-status`,
      suspiciousDevices: `/customer-support/hacker-analysis/suspicious-devices`,
      suspiciousDevicesExport: `/customer-support/hacker-analysis/suspicious-devices/export`,
      suspiciousDevicesScan: `/customer-support/hacker-analysis/suspicious-devices/scan`,
      suspiciousDeviceRecord: (deviceKey: string) =>
        `/customer-support/hacker-analysis/suspicious-devices/records/${encodeURIComponent(deviceKey)}`,
      suspiciousDeviceRecordStatus: (deviceKey: string) =>
        `/customer-support/hacker-analysis/suspicious-devices/records/${encodeURIComponent(deviceKey)}/status`,
      suspiciousDeviceRecordComments: (deviceKey: string) =>
        `/customer-support/hacker-analysis/suspicious-devices/records/${encodeURIComponent(deviceKey)}/comments`,
      suspiciousDevicesBulkStatus: `/customer-support/hacker-analysis/suspicious-devices/bulk-status`,
      gdprExports: {
        trigger: `/customer-support/gdpr-exports`,
        list: `/customer-support/gdpr-exports`,
        byId: (id: string) => `/customer-support/gdpr-exports/${encodeURIComponent(id)}`,
        download: (id: string) =>
          `/customer-support/gdpr-exports/${encodeURIComponent(id)}/download`,
      },
    },
    cloudflareFirebaseRemoteConfig: {
      remoteConfig: `/cloudflare-firebase-remote-config/remote-config`,
      rule: `/cloudflare-firebase-remote-config/rule`,
      ruleStatistics: `/cloudflare-firebase-remote-config/rule/statistics`,
      rotateSecret: `/cloudflare-firebase-remote-config/rotate-secret`,
      auditLogs: `/cloudflare-firebase-remote-config/audit-logs`,
    },
    accounts: {
      bySponsorId: (sponsorId: string) => `/accounts/by-sponsor/${sponsorId}`,
    },
    v2: {
      tempPasswords: {
        list: `/v2/temp-passwords`,
        export: `/v2/temp-passwords/export`,
      },
    },
    fleetVirtualEmails: {
      list: `/fleet-virtual-emails`,
      fetch: `/fleet-virtual-emails/fetch`,
      byId: (emailId: string) => `/fleet-virtual-emails/${emailId}`,
      body: (emailId: string) => `/fleet-virtual-emails/${emailId}/body`,
    },
    fleetHpForm: {
      submissions: {
        list: `/fleet-hp-form/submissions`,
        byId: (submissionId: string | number) => `/fleet-hp-form/submissions/${submissionId}`,
        export: `/fleet-hp-form/submissions/export`,
      },
      v2: {
        techFormTypes: `/fleet-hp-form/v2/tech-form-types`,
        submissions: {
          list: `/fleet-hp-form/v2/submissions`,
          byId: (submissionId: string | number) => `/fleet-hp-form/v2/submissions/${submissionId}`,
          comments: {
            create: (submissionId: string | number) =>
              `/fleet-hp-form/v2/submissions/${submissionId}/comments`,
            delete: (submissionId: string | number, commentId: string | number) =>
              `/fleet-hp-form/v2/submissions/${submissionId}/comments/${commentId}`,
          },
          responsiblePerson: {
            assign: (submissionId: string | number) =>
              `/fleet-hp-form/v2/submissions/${submissionId}/responsible-person`,
            remove: (submissionId: string | number) =>
              `/fleet-hp-form/v2/submissions/${submissionId}/responsible-person`,
          },
        },
        fetch: {
          portal: `/fleet-hp-form/v2/fetch/portal`,
        },
      },
    },

    /** WordPress fleet onboarding funnel — Firestore session docs (rp-users). */
    fleetMarketingFunnel: {
      sessions: {
        list: `/fleet-funnel/v2/portal/sessions`,
        byId: (sessionId: string) => `/fleet-funnel/v2/portal/sessions/${sessionId}`,
        manualFleetRetry: (sessionId: string) =>
          `/fleet-funnel/v2/portal/sessions/${sessionId}/manual-fleet-retry`,
      },
    },

    /**
     * Ops side of the public fleet checkout (`/checkout/fleet`, funnel V5).
     *
     * Note the prefix: `/fleet-checkout/v1/` is the *unauthenticated* public
     * router in rp-users, so every operator endpoint lives under
     * `/fleet-checkout/portal/` (Firebase auth + `ryd` user group).
     */
    fleetCheckout: {
      orders: {
        list: `/fleet-checkout/portal/orders`,
        byId: (orderId: string) => `/fleet-checkout/portal/orders/${orderId}`,
        retryProvisioning: (orderId: string) =>
          `/fleet-checkout/portal/orders/${orderId}/retry-provisioning`,
      },
      sessions: {
        list: `/fleet-checkout/portal/sessions`,
        byId: (sessionId: string) =>
          `/fleet-checkout/portal/sessions/${encodeURIComponent(sessionId)}`,
      },
      statistics: `/fleet-checkout/portal/statistics`,
      callbacks: {
        list: `/fleet-checkout/portal/callbacks`,
        byId: (callbackId: string) =>
          `/fleet-checkout/portal/callbacks/${encodeURIComponent(callbackId)}`,
        notes: (callbackId: string) =>
          `/fleet-checkout/portal/callbacks/${encodeURIComponent(callbackId)}/notes`,
      },
    },

    /**
     * Portal side of the public voucher campaigns (`/promo/:slug`).
     *
     * Note the prefix: `/promo/` is the *unauthenticated* public router in
     * rp-users, so every operator endpoint lives under `/promo-campaigns/`.
     */
    promoCampaigns: {
      campaigns: `/promo-campaigns/v1/campaigns`,
      statistics: `/promo-campaigns/v1/statistics`,
      orderStatistics: `/promo-campaigns/v1/order-statistics`,
      registrations: `/promo-campaigns/v1/registrations`,
      registration: `/promo-campaigns/v1/registration`,
      emailContent: `/promo-campaigns/v1/registration/email-content`,
      registrationWallet: `/promo-campaigns/v1/registration/wallet`,
      registrationOrders: `/promo-campaigns/v1/registration/orders`,
      registrationOrderStatistics: `/promo-campaigns/v1/registration/order-statistics`,
      recheck: `/promo-campaigns/v1/registration/recheck`,
      applyTag: `/promo-campaigns/v1/registration/apply-tag`,
      activate: `/promo-campaigns/v1/registration/activate`,
      removeTag: `/promo-campaigns/v1/registration/remove-tag`,
      approve: `/promo-campaigns/v1/registration/approve`,
      reject: `/promo-campaigns/v1/registration/reject`,
      changeTier: `/promo-campaigns/v1/registration/change-tier`,
      resendDecisionEmail: `/promo-campaigns/v1/registration/resend-decision-email`,
      runAccountCheck: `/promo-campaigns/v1/account-check/run`,
      bulkDecisions: `/promo-campaigns/v1/bulk-decisions`,
      bulkDecisionRun: `/promo-campaigns/v1/bulk-decisions/run`,
      bulkDecisionRetryFailed: `/promo-campaigns/v1/bulk-decisions/retry-failed`,
      bulkDecisionContinue: `/promo-campaigns/v1/bulk-decisions/continue`,
    },

    savingsCalculatorSessions: {
      list: `/fleet-funnel/v2/portal/calculator-sessions`,
      byId: (sessionId: string) => `/fleet-funnel/v2/portal/calculator-sessions/${sessionId}`,
      retryProvisioning: (sessionId: string) =>
        `/fleet-funnel/v2/portal/calculator-sessions/${sessionId}/retry-provisioning`,
    },

    feedback: {
      list: `/feedback`,
      byId: (id: string) => `/feedback/${id}`,
      comments: (id: string) => `/feedback/${id}/comments`,
      commentById: (id: string, commentId: string) => `/feedback/${id}/comments/${commentId}`,
      attachmentsUpload: '/feedback/attachments',
      attachmentsByFeedbackId: (feedbackId: string) => `/feedback/${feedbackId}/attachments`,
      attachmentUrlById: (attachmentId: string) =>
        `/feedback/attachments/${attachmentId}/download-url`,
    },

    files: {
      upload: `/files/upload`,
    },
  },

  rpIntegration: {
    /** ryd Ops → partner-fuel-card-service `/ops/*` (ryd + fuel_card_ops; rp-integrations allow-list). */
    partnerFuelCards: {
      orders: `/fuel-cards/ops/orders`,
      order: (orderId: string) => `/fuel-cards/ops/orders/${encodeURIComponent(orderId)}`,
      orderAction: (orderId: string, action: string) =>
        `/fuel-cards/ops/orders/${encodeURIComponent(orderId)}/${action}`,
      files: `/fuel-cards/ops/files`,
      file: (fileId: string) => `/fuel-cards/ops/files/${encodeURIComponent(fileId)}`,
      fileDownload: (fileId: string) =>
        `/fuel-cards/ops/files/${encodeURIComponent(fileId)}/download`,
      fileRejected: (fileId: string) =>
        `/fuel-cards/ops/files/${encodeURIComponent(fileId)}/rejected-by-provider`,
      sequence: (programId: string) => `/fuel-cards/ops/sequences/${encodeURIComponent(programId)}`,
      buildNow: `/fuel-cards/ops/build-now`,
      buildCancelOnlyNow: `/fuel-cards/ops/build-cancel-only-now`,
      pollNow: `/fuel-cards/ops/poll-now`,
      items: `/fuel-cards/ops/items`,
      item: (itemId: string) => `/fuel-cards/ops/items/${encodeURIComponent(itemId)}`,
      manualImport: `/fuel-cards/ops/manual-import`,
      programs: `/fuel-cards/ops/programs`,
      program: (programId: string) => `/fuel-cards/ops/programs/${encodeURIComponent(programId)}`,
      sftpEntries: `/fuel-cards/ops/sftp/entries`,
      sftpEntry: (folder: string, name: string) =>
        `/fuel-cards/ops/sftp/entries/${encodeURIComponent(folder)}/${encodeURIComponent(name)}`,
      sftpEntryDownload: (folder: string, name: string) =>
        `/fuel-cards/ops/sftp/entries/${encodeURIComponent(folder)}/${encodeURIComponent(name)}/download`,
    },
    customerSupport: {
      usersSearch: `/customer-support/users/search`,
      usersBulkAction: `/customer-support/users/bulk-action`,
      ordersBulkByAccountIds: `/customer-support/orders/bulk-by-account-ids`,
      // When / how / who an account was deleted (consolidated audit record)
      accountDeletionRecord: (accountId: string) =>
        `/customer-support/accounts/${encodeURIComponent(accountId)}/deletion-record`,
      // Deletion overview page (gdpr_admin): search by deleted e-mail / ids, recent list, on-demand sync
      accountDeletionsSearch: `/customer-support/account-deletions`,
      accountDeletionsRecent: `/customer-support/account-deletions/recent`,
      accountDeletionsReconcile: `/customer-support/account-deletions/reconcile`,
      accountDeletionsRuns: `/customer-support/account-deletions/runs`,
      accountDeletionConfirmationEmail: (accountId: string) =>
        `/customer-support/account-deletions/${encodeURIComponent(accountId)}/confirmation-email`,
    },
    auditInteractions: {
      search: `/audit-interactions/search`,
      webhookLogsEnvironments: `/audit-interactions/webhook-logs/environments`,
      webhookLogsSearch: `/audit-interactions/webhook-logs/search`,
      webhookLogsCount: `/audit-interactions/webhook-logs/count`,
      webhookLogsPartners: `/audit-interactions/webhook-logs/partners`,
      byOrderUuid: `/audit-interactions/by-order-uuid`,
      downloadGcsFile: `/audit-interactions/download-gcs-file`,
    },
    partnerAlerts: {
      list: `/partner-alerts`,
      byId: (eventId: string) => `/partner-alerts/${encodeURIComponent(eventId)}`,
      orderById: (eventId: string) => `/partner-alerts/${encodeURIComponent(eventId)}/order`,
      comments: (eventId: string) => `/partner-alerts/${encodeURIComponent(eventId)}/comments`,
      commentById: (eventId: string, commentId: string) =>
        `/partner-alerts/${encodeURIComponent(eventId)}/comments/${encodeURIComponent(commentId)}`,
      reviewStatus: (eventId: string) =>
        `/partner-alerts/${encodeURIComponent(eventId)}/review-status`,
      retryExport: (eventId: string) =>
        `/partner-alerts/${encodeURIComponent(eventId)}/retry-export`,
      retryHistory: (eventId: string) =>
        `/partner-alerts/${encodeURIComponent(eventId)}/retry-history`,
      checkExportSuccess: (eventId: string) =>
        `/partner-alerts/${encodeURIComponent(eventId)}/check-export-success`,
    },
    orders: {
      bySponsorId: `/orders/by-sponsor-id`,
      byApiGateway: `/orders/by-api-gateway`,
      customerSupportByApiGateway: `/orders/customer-support/by-api-gateway`,
      recentByPartner: `/orders/recent-by-partner`,
      exportCsv: `/orders/export-csv`,
      byPoiId: `/orders/by-poi-id`,
      search: `/orders/search`,
    },
    fleetPartnerOrders: `/fleet-partner-orders`,
    gasStations: {
      addressAutoCompletion: `/gas-stations/address-auto-completion`,
      priceUpdates: `/gas-stations/price-updates`,
      stationStatus: `/gas-stations/station-status`,
      stationStatusHistory: `/gas-stations/station-status-history`,
      activeStationsDownload: `/gas-stations/active/download`,
    },
    operationalExcellence: {
      configs: `/operational-excellence/configs`,
      config: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}`,
      testRun: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/test-run`,
      testMessage: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/test-message`,
      sampleAlert: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/sample-alert`,
      partnerDomains: (userGroup: string) =>
        `/operational-excellence/partner-domains/${encodeURIComponent(userGroup)}`,
      history: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/history`,
      checkGuide: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/check-guide`,
      versions: (partnerKey: string) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/versions`,
      restoreVersion: (partnerKey: string, version: number) =>
        `/operational-excellence/configs/${encodeURIComponent(partnerKey)}/versions/${version}/restore`,
      myConfig: `/operational-excellence/my-config`,
      myConfigVersions: `/operational-excellence/my-config/versions`,
      myConfigRestoreVersion: (version: number) =>
        `/operational-excellence/my-config/versions/${version}/restore`,
      myConfigHistory: `/operational-excellence/my-config/history`,
      myConfigCheckGuide: `/operational-excellence/my-config/check-guide`,
    },
    poiStatistics: {
      providers: `/poi-statistics/providers`,
      refresh: `/poi-statistics/refresh`,
      runs: `/poi-statistics/runs`,
      run: (runId: string) => `/poi-statistics/runs/${encodeURIComponent(runId)}`,
      resumeRun: (runId: string) => `/poi-statistics/runs/${encodeURIComponent(runId)}/resume`,
      forcePublishRun: (runId: string) =>
        `/poi-statistics/runs/${encodeURIComponent(runId)}/force-publish`,
    },
    fleetDataAnalytics: {
      sponsors: `/fleet-data-analytics/sponsors`,
      accounts: `/fleet-data-analytics/accounts`,
      sponsorFeatures: `/fleet-data-analytics/sponsor-features`,
      odooPartners: `/fleet-data-analytics/odoo-partners`,
      purchaseLimits: `/fleet-data-analytics/purchase-limits`,
      sponsorFeatureGroupings: `/fleet-data-analytics/sponsor-feature-groupings`,
      licensePlateStatistics: `/fleet-data-analytics/license-plate-statistics`,
      monthlyLimitStatistics: `/fleet-data-analytics/monthly-limit-statistics`,
      query: `/fleet-data-analytics/query`,
      dimensions: `/fleet-data-analytics/dimensions`,
      metrics: `/fleet-data-analytics/metrics`,
      charts: {
        licensePlate: `/fleet-data-analytics/charts/license-plate`,
        sponsorFeaturePack: `/fleet-data-analytics/charts/sponsor-feature-pack`,
        monthlyLimit: `/fleet-data-analytics/charts/monthly-limit`,
        sponsorStatus: `/fleet-data-analytics/charts/sponsor-status`,
        weeklyLimit: `/fleet-data-analytics/charts/weekly-limit`,
      },
      featureDistributions: `/fleet-data-analytics/feature-distributions`,
    },
    reconciliationProcess: {
      egOrders: `/reconciliation-process/eg/orders`,
      dashboardOverview: `/reconciliation-process/dashboard/overview`,
      siteSyncJobs: `/reconciliation-process/dashboard/site-sync-jobs`,
      transactionJobs: `/reconciliation-process/dashboard/transaction-jobs`,
      jobMessages: (jobType: string, jobId: string) =>
        `/reconciliation-process/dashboard/job-messages/${jobType}/${jobId}`,
    },
    fileManagement: {
      list: `/file-management/list`,
      listPaginated: `/file-management/list-paginated`,
      search: `/file-management/search`,
      triggerReconciliation: `/file-management/trigger-reconciliation`,
      download: `/file-management/download`,
      recentFiles: `/file-management/recent-files`,
      folder: `/file-management/folder`,
      delete: `/file-management/file`,
      upload: `/file-management/upload`,
      updateMetadata: `/file-management/metadata`,
      customMetadata: `/file-management/custom-metadata`,
    },
    productMetadata: {
      normalizedNames: `/product-metadata/normalized-names`,
      productGroups: `/product-metadata/product-groups`,
      marketingNames: `/product-metadata/marketing-names`,
      brands: `/product-metadata/brands`,
    },
    partnerAnalyticsMetrics: {
      termsHistogramApiGatewayHttpRequests: `/partner-analytics-metrics/terms-histogram/api-gateway-http-requests`,
      termsHistogramRequestUrlNormalised: `/partner-analytics-metrics/terms-histogram/request-url-normalized`,
      metricsNrOfHttpRequests: `/partner-analytics-metrics/metrics-nr-of-http-requests`,
      termsHistogramCreatedOrders: `/partner-analytics-metrics/terms-histogram/created-orders`,
      orderStatesFailures: `/partner-analytics-metrics/order-states/failures`,
      termsHistogramCreatedOrdersOrderState: `/partner-analytics-metrics/terms-histogram/created-orders/order-state`,
      termsHistogramCreatedOrdersOrderStateErrorRate: `/partner-analytics-metrics/terms-histogram/created-orders/order-state-error-rate`,
      metricsNrOfCreatedOrders: `/partner-analytics-metrics/metrics-nr-of-created-orders`,
      orders: {
        apiGatewayHttpRequests: `/partner-analytics-metrics/orders/api-gateway-http-requests`,
        popEvents: `/partner-analytics-metrics/orders/pop-events`,
        errorLogMessages: `/partner-analytics-metrics/orders/error-log-messages`,
        createdOrdersByPartner: `/partner-analytics-metrics/orders/created-orders-by-partner`,
        partnerSystemInteractions: `/partner-analytics-metrics/orders/partner-system-interactions`,
        popEventsEvSessions: `/partner-analytics-metrics/orders/pop-events/ev-sessions`,
        popEventsEvSessionsEvents: `/partner-analytics-metrics/orders/pop-events/ev-sessions/events`,
        popEventsEvSessionsCdrRecords: `/partner-analytics-metrics/orders/pop-events/ev-sessions/cdr-records`,
      },
      userAgents: `/partner-analytics-metrics/user-agents`,
    },
    orderLookup: {
      auditLogs: `/order-lookup/audit-logs`,
      auditAnalytics: `/order-lookup/audit-logs/analytics`,
    },
    connectTracking: {
      journey: `/connect-tracking/journey`,
      partnerJourney: `/connect-tracking/partner/journey`,
      partners: `/connect-tracking/partners`,
      eventBody: `/connect-tracking/event-body`,
      partnerEventBody: `/connect-tracking/partner/event-body`,
    },
    troubleshooting: {
      orderDetails: {
        byOrderUuid: (orderUuid: string) => `/troubleshooting/order-details/${orderUuid}`,
        userSponsorDetails: (orderUuid: string) =>
          `/troubleshooting/order-details/${orderUuid}/user-sponsor-details`,
        importantApiCalls: (orderUuid: string) =>
          `/troubleshooting/order-details/${orderUuid}/important-api-calls`,
        /** CS-02/04/06: warehouse failure analysis, transaction timeline, reconstructed app message. */
        diagnostics: (orderUuid: string) => `/customer-support/orders/${orderUuid}/diagnostics`,
      },
    },
    partners: {
      legalInfo: `/partners/legal-info`,
    },
    partnerFiles: {
      list: `/partner-files`,
      download: (fileId: string) => `/partner-files/download/${fileId}`,
    },
    odooModelExplorer: {
      fields: (modelName: string) => `/odoo-model-explorer/fields/${modelName}`,
      searchRead: `/odoo-model-explorer/search-read`,
      searchReadPumpPriceBased: `/odoo-model-explorer/search-read-pump-price-based`,
    },
    v2: {
      orders: {
        byDataWarehouseId: `/v2/orders/by-data-warehouse-id`,
        byDataWarehouseIdAndOrderUuid: (orderUuid: string) =>
          `/v2/orders/by-data-warehouse-id-and-order-uuid/${orderUuid}`,
      },
    },
    reconciliationProxy: {
      jobExecutions: {
        byEnvironment: (environment: string) =>
          `/reconciliation-proxy/${environment}/job-executions`,
        stepsByEnvironmentAndJobExecutionId: (environment: string, jobExecutionId: string) =>
          `/reconciliation-proxy/${environment}/job-executions/${jobExecutionId}/steps`,
        ordersByEnvironmentAndJobExecutionId: (environment: string, jobExecutionId: string) =>
          `/reconciliation-proxy/${environment}/job-executions/${jobExecutionId}/orders`,
      },
      orders: `/reconciliation-proxy/orders`,
      reconciliationResults: {
        byEnvironment: (environment: string) =>
          `/reconciliation-proxy/${environment}/reconciliation-results`,
      },
      reconciliationFiles: {
        download: (environment: string) =>
          `/reconciliation-proxy/${environment}/reconciliation-files/download`,
      },
    },
    b2b: {
      auditEvents: {
        byOrder: `/b2b/audit-events/by-order`,
      },
    },
    emailReader: {
      emails: `/email-reader/emails`,
      emailById: (emailId: string) => `/email-reader/emails/${emailId}`,
      threads: {
        byId: (threadId: string) => `/email-reader/threads/${threadId}`,
      },
      attachments: {
        download: (emailId: string, attachmentId: string) =>
          `/email-reader/emails/${emailId}/attachments/${attachmentId}`,
      },
    },
    /** Mia training cases (spec v10 §15): ryd + ai_operations_* roles (D-61). */
    aiTraining: {
      cases: `/ai-training/cases`,
      caseFromMessage: `/ai-training/cases/from-message`,
      caseIdentifiers: (caseId: string) =>
        `/ai-training/cases/${encodeURIComponent(caseId)}/identifiers`,
      case: (caseId: string) => `/ai-training/cases/${encodeURIComponent(caseId)}`,
      caseRun: (caseId: string) => `/ai-training/cases/${encodeURIComponent(caseId)}/run`,
      caseCapture: (caseId: string) =>
        `/ai-training/cases/${encodeURIComponent(caseId)}/capture-session`,
      items: `/ai-training/items`,
      item: (itemId: string) => `/ai-training/items/${encodeURIComponent(itemId)}`,
      itemLifecycle: (itemId: string, action: 'approve' | 'retire') =>
        `/ai-training/items/${encodeURIComponent(itemId)}/${action}`,
      activate: `/ai-training/activate`,
      snapshots: `/ai-training/snapshots`,
      snapshotReactivate: (consumer: string, version: number) =>
        `/ai-training/snapshots/${encodeURIComponent(consumer)}/${encodeURIComponent(String(version))}/reactivate`,
      snapshotVerify: (consumer: string, version: number) =>
        `/ai-training/snapshots/${encodeURIComponent(consumer)}/${encodeURIComponent(String(version))}/verify`,
      preview: `/ai-training/preview`,
      runs: `/ai-training/regression-runs`,
      run: (runId: string) => `/ai-training/regression-runs/${encodeURIComponent(runId)}`,
      commands: `/ai-training/commands`,
      consumerOptions: `/ai-training/consumer-options`,
    },
    /** Knowledge Center viewer (read-only): ryd + ai_operations_* roles. Article key goes in `?key=`. */
    aiKnowledge: {
      corpora: `/ai-knowledge/corpora`,
      articles: (corpus: string) => `/ai-knowledge/corpora/${encodeURIComponent(corpus)}/articles`,
      article: (corpus: string) => `/ai-knowledge/corpora/${encodeURIComponent(corpus)}/article`,
      retrievalPreview: `/ai-knowledge/retrieval-preview`,
    },
    /** Request Inbox (T-03, phase 1 dry run, sources §13): ryd + ai_operations_* roles (D-44). */
    requestInbox: {
      overview: `/request-inbox/overview`,
      runs: `/request-inbox/runs`,
      messages: `/request-inbox/messages`,
      message: (messageId: string) => `/request-inbox/messages/${encodeURIComponent(messageId)}`,
      thread: (threadId: string) => `/request-inbox/threads/${encodeURIComponent(threadId)}`,
      routing: (messageId: string) =>
        `/request-inbox/messages/${encodeURIComponent(messageId)}/routing`,
      routingReview: (messageId: string) =>
        `/request-inbox/messages/${encodeURIComponent(messageId)}/routing/review`,
      correction: (messageId: string) =>
        `/request-inbox/messages/${encodeURIComponent(messageId)}/correction`,
      attachment: (messageId: string, index: number) =>
        `/request-inbox/messages/${encodeURIComponent(messageId)}/attachments/${index}`,
      taxonomy: `/request-inbox/taxonomy`,
      taxonomyTerm: (termId: string) => `/request-inbox/taxonomy/${encodeURIComponent(termId)}`,
      taxonomyMerge: (termId: string) =>
        `/request-inbox/taxonomy/${encodeURIComponent(termId)}/merge`,
      suggestedTerms: `/request-inbox/suggested-terms`,
      suggestedTermMap: (suggestionId: string) =>
        `/request-inbox/suggested-terms/${encodeURIComponent(suggestionId)}/map`,
      routingRules: `/request-inbox/routing-rules`,
      routingRule: (ruleId: string) => `/request-inbox/routing-rules/${encodeURIComponent(ruleId)}`,
      routingRulePreview: (ruleId: string) =>
        `/request-inbox/routing-rules/${encodeURIComponent(ruleId)}/preview`,
      teamMap: `/request-inbox/team-map`,
      teamMapEntry: (teamCode: string) => `/request-inbox/team-map/${encodeURIComponent(teamCode)}`,
      odooCatalog: `/request-inbox/odoo-catalog`,
      sources: `/request-inbox/sources`,
      source: (sourceId: string) => `/request-inbox/sources/${encodeURIComponent(sourceId)}`,
      feedback: `/request-inbox/feedback`,
      feedbackDecision: (feedbackId: string, decision: 'approve' | 'reject') =>
        `/request-inbox/feedback/${encodeURIComponent(feedbackId)}/${decision}`,
    },
    orderAnalytics: `/order-analytics`,
    allguth: {
      failedOrders: `/allguth/failed-orders`,
      failedOrdersDownload: `/allguth/failed-orders/download`,
    },
    places: {
      carWashStations: {
        list: `/places/car-wash-stations`,
        brands: `/places/car-wash-stations/brands`,
        download: `/places/car-wash-stations/download`,
      },
    },
    invoiceCancellationModification: {
      search: (orderUuid: string) => `/invoice-cancellation-modification/search/${orderUuid}`,
      cancel: (orderUuid: string) => `/invoice-cancellation-modification/cancel/${orderUuid}`,
      queueMessage: (orderUuid: string) =>
        `/invoice-cancellation-modification/queue-message/${orderUuid}`,
      queueMessageCreate: `/invoice-cancellation-modification/queue-message`,
    },
    einvoiceValidationCases: {
      list: `/einvoice-validation/cases`,
      file: (caseId: string, fileId: string) =>
        `/einvoice-validation/cases/${encodeURIComponent(caseId)}/files/${encodeURIComponent(fileId)}`,
      // Staging backend only: posts the case's anonymised queue.message to Odoo staging.
      replay: (caseId: string) => `/einvoice-validation/cases/${encodeURIComponent(caseId)}/replay`,
    },
    // Corrected Factur-X copy from current Odoo totals; read-only Odoo, nothing is written back.
    einvoiceRepair: {
      order: `/einvoice-repair/order`,
      upload: `/einvoice-repair/upload`,
    },
    listPriceNotificationContacts: {
      list: `/list-price-notification-contacts`,
      statistics: `/list-price-notification-contacts/statistics`,
      byId: (id: string | number) => `/list-price-notification-contacts/${id}`,
      bulkPopulate: `/list-price-notification-contacts/bulk-populate`,
    },
    listPrice: {
      main: `/list-price`,
      exportCsv: `/list-price/export-csv`,
      emailPreview: `/list-price/email-preview`,
      emailPreviewTestSend: `/list-price/email-preview/test-send`,
      partnerDiscounts: {
        list: `/list-price/partner-discounts`,
        byId: (id: string | number) => `/list-price/partner-discounts/${id}`,
      },
      priceAdjustments: {
        list: `/list-price/price-adjustments`,
        byId: (id: string | number) => `/list-price/price-adjustments/${id}`,
      },
      dailySummary: `/list-price/daily-summary`,
      messageDetails: (messageId: string) => `/list-price/message-details/${messageId}`,
    },
    tt4Proxy: {
      v4: {
        places: `/tt4-proxy/v4/places`,
        placeSearch: `/tt4-proxy/v4/place-search`,
        shopDetails: {
          byId: (shopId: string) => `/tt4-proxy/v4/shop-details/${encodeURIComponent(shopId)}`,
        },
        shops: {
          byId: (shopId: string) => `/tt4-proxy/v4/shops/${encodeURIComponent(shopId)}`,
        },
        order: {
          create: `/tt4-proxy/v4/order`,
          authorize: (orderId: string) => `/tt4-proxy/v4/order/${orderId}/authorize`,
          byId: (orderId: string) => `/tt4-proxy/v4/order/${orderId}`,
        },
      },
    },
    emailDomains: {
      byListType: (listType: string) => `/email-domains/${listType}`,
      byListTypeAndId: (listType: string, id: string | number) =>
        `/email-domains/${listType}/${id}`,
    },
    invoiceDataConsistencyCheck: {
      comparisonResults: {
        list: `/invoice-data-consistency-check/comparison-results`,
        comments: (dayId: string) =>
          `/invoice-data-consistency-check/comparison-results/${dayId}/comments`,
      },
      comparisonTrigger: `/invoice-data-consistency-check/comparison-trigger`,
    },
    invoiceRetry: {
      failedRequests: `/invoice-retry/failed-requests`,
      customerSupportFailedScan: `/invoice-retry/failed-requests/customer-support-scan`,
      retry: (orderUuid: string, id: string | number) => `/invoice-retry/retry/${orderUuid}/${id}`,
      queueMessages: {
        byUuid: (uuid: string) => `/invoice-retry/queue-messages/${uuid}`,
      },
      triggerInvoice: (uuid: string) => `/invoice-retry/trigger-invoice/${uuid}`,
    },
    invoiceReports: {
      b2cCountryProduct: `/invoice/b2c-country-product-report`,
    },
    odooSaleOrder: {
      queueMessages: (orderUuid: string) => `/odoo/sale-order/queue-messages/${orderUuid}`,
      queueMessageApiLogs: (orderUuid: string) =>
        `/odoo/sale-order/queue-message-api-logs/${orderUuid}`,
      rejectedQueueMessageBody: (orderUuid: string) =>
        `/odoo/sale-order/rejected-queue-message-body/${orderUuid}`,
      queueJobsByQueueMessage: (queueMessageId: string | number) =>
        `/odoo/sale-order/queue-jobs/by-queue-message/${queueMessageId}`,
      queueJobPrioritize: `/odoo/sale-order/queue-jobs/prioritize`,
    },
    rydMarketing: {
      awinPush: {
        notifications: `/ryd-marketing/awin-push/notifications`,
        mailchimpDetails: (pushId: string) =>
          `/ryd-marketing/awin-push/notifications/${pushId}/mailchimp-details`,
      },
    },
    partnerLinks: {
      list: `/partner-links`,
      byId: (id: string | number) => `/partner-links/${id}`,
    },
    customerEventsAnalytics: {
      events: `/customer-events-analytics/events`,
      timeline: `/customer-events-analytics/timeline`,
      timelineGraylogMessage: `/customer-events-analytics/timeline/graylog-message`,
      accountActivities: `/customer-events-analytics`,
      deviceInfoByAccountId: `/customer-events-analytics/device-info-by-account-id`,
      orderIdsByAccountId: `/customer-events-analytics/order-ids-by-account-id`,
    },
    deeplinkManagement: {
      generate: `/deeplink-management/generate`,
    },
    apiPartnerDiscounts: {
      list: `/api-partner-discounts`,
      byId: (id: string) => `/api-partner-discounts/${id}`,
      lookup: `/api-partner-discounts/lookup`,
    },
    partnerReports: {
      exportOrders: `/partner-reports/export-orders`,
      list: `/partner-reports`,
      download: (reportId: string) => `/partner-reports/download/${reportId}`,
      content: (reportId: string) => `/partner-reports/content/${reportId}`,
    },
    partnerDocuments: {
      list: `/partner-documents`,
      upload: `/partner-documents`,
      partnerUpload: `/partner-documents/partner-upload`,
      update: (documentId: string) => `/partner-documents/${documentId}`,
      content: (documentId: string) => `/partner-documents/content/${documentId}`,
      download: (documentId: string) => `/partner-documents/download/${documentId}`,
    },
    /** Circle K POS reporting (DFS PCS insertsale) monitor and control, ryd-internal. */
    circleKPos: {
      environments: '/bmw-fleet/circle-k-pcs/environments',
      activity: '/bmw-fleet/circle-k-pcs/activity',
      activityEvent: (eventId: string) =>
        `/bmw-fleet/circle-k-pcs/activity/${encodeURIComponent(eventId)}`,
    },
    payOperations: {
      receiptUploads: {
        config: '/pay-operations/receipt-uploads/config',
        list: '/pay-operations/receipt-uploads',
        order: (uuid: string) =>
          `/pay-operations/receipt-uploads/order/${encodeURIComponent(uuid)}`,
        attempt: (id: string) => `/pay-operations/receipt-uploads/${encodeURIComponent(id)}`,
        result: (id: string) => `/pay-operations/receipt-uploads/${encodeURIComponent(id)}/result`,
        diagnosis: (id: string) =>
          `/pay-operations/receipt-uploads/${encodeURIComponent(id)}/diagnosis`,
        image: (id: string) => `/pay-operations/receipt-uploads/${encodeURIComponent(id)}/image`,
        audit: '/pay-operations/receipt-uploads/audit-logs',
      },
      orderAmendments: {
        cases: `/pay-operations/order-amendments/cases`,
        refreshAll: `/pay-operations/order-amendments/cases/refresh-all`,
        caseByUuid: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}`,
        refresh: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/refresh`,
        preview: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/preview`,
        execute: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/execute`,
        attempts: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/attempts`,
        assign: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/assign`,
        unassign: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/unassign`,
        status: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/status`,
        caseIncidents: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/incidents`,
        enums: `/pay-operations/order-amendments/enums`,
        audit: `/pay-operations/order-amendments/audit`,
        refund: (uuid: string) => `/pay-operations/order-amendments/cases/${uuid}/refund`,
        driveAwayPayment: (uuid: string) =>
          `/pay-operations/order-amendments/cases/${uuid}/drive-away-payment`,
        driveAwayInvoice: (uuid: string) =>
          `/pay-operations/order-amendments/cases/${uuid}/drive-away-invoice`,
        triggerInvoiceGeneration: (uuid: string) =>
          `/pay-operations/order-amendments/cases/${uuid}/invoice-generation`,
        updateOdooPayment: (uuid: string) =>
          `/pay-operations/order-amendments/cases/${uuid}/odoo-payment`,
        bulkJobs: `/pay-operations/order-amendments/bulk-jobs`,
        bulkJobById: (id: string) => `/pay-operations/order-amendments/bulk-jobs/${id}`,
        bulkJobRetry: (id: string) =>
          `/pay-operations/order-amendments/bulk-jobs/${id}/retry-failures`,
        incidents: `/pay-operations/order-amendments/incidents`,
        incidentById: (id: string) => `/pay-operations/order-amendments/incidents/${id}`,
        incidentOrders: (id: string) => `/pay-operations/order-amendments/incidents/${id}/orders`,
        incidentCases: (id: string) => `/pay-operations/order-amendments/incidents/${id}/cases`,
        incidentOrder: (id: string, uuid: string) =>
          `/pay-operations/order-amendments/incidents/${id}/orders/${uuid}`,
        assignmentSessions: `/pay-operations/order-amendments/assignment-sessions`,
        assignmentSessionById: (id: string) =>
          `/pay-operations/order-amendments/assignment-sessions/${id}`,
        clearManualClassification: (uuid: string) =>
          `/pay-operations/order-amendments/cases/${uuid}/manual-classification`,
      },
    },
  },

  partnerPerformanceGuardian: {
    // `activeGasStations` removed: the petrol station export now lives in rp-integrations as
    // `rpIntegration.gasStations.activeStationsDownload`, behind the per-user Firebase session.
    alerts: {
      list: `/alerts`,
      byId: (alertId: string | number) => `/alerts/${alertId}`,
      responseStatus: (alertId: string | number) => `/alerts/${alertId}/response_status`,
      note: (alertId: string | number) => `/alerts/${alertId}/note`,
    },
    cashierSystemConfigurations: {
      list: `/cashier-system-configurations`,
      byId: (id: string | number) => `/cashier-system-configurations/${id}`,
      delete: (id: string | number) => `/cashier-system-configurations/${id}`,
    },
    constants: {
      cashierSystems: `/constants/cashier-systems`,
      mocPartners: `/constants/moc-partners`,
    },
    mocPartnerConfigurations: {
      list: `/moc-partner-configurations`,
      byId: (id: string | number) => `/moc-partner-configurations/${id}`,
      delete: (id: string | number) => `/moc-partner-configurations/${id}`,
    },
    guardianChecks: {
      byId: (guardianCheckId: string | number) => `/guardian-checks/${guardianCheckId}`,
      aggregations: {
        maxId: `/guardian-checks/aggregations/max-id`,
      },
    },
    gasStations: {
      byId: (poiId: string | number) => `/gas-stations/${poiId}`,
      offlineHistory: (poiId: string | number) => `/gas-stations/${poiId}/offline-history`,
    },
    offlineGasStations: `/offline-gas-stations`,
  },

  partnerAnalytics: {
    missingInvoiceApiCalls: `/missing-invoice-api-calls`,
    ppTokenUsage: `/pp-token-usage`,
  },

  partnerAnalyticsMetricsAlert: {
    metricsNrOfHttpRequests: `/partner-analytics-metrics/metrics-nr-of-http-requests`,
    termsHistogramApiGatewayHttpRequests: `/partner-analytics-metrics/terms-histogram/api-gateway-http-requests`,
    termsHistogramRequestUrlNormalised: `/partner-analytics-metrics/terms-histogram/request-url-normalized`,
    // Order Insights Analytics (migrated from rp-integrations)
    metricsNrOfCreatedOrders: `/partner-analytics-metrics/metrics-nr-of-created-orders`,
    termsHistogramCreatedOrders: `/partner-analytics-metrics/terms-histogram/created-orders`,
    termsHistogramCreatedOrdersOrderState: `/partner-analytics-metrics/terms-histogram/created-orders/order-state`,
    termsHistogramCreatedOrdersOrderStateErrorRate: `/partner-analytics-metrics/terms-histogram/created-orders/order-state-error-rate`,
    orderStatesFailures: `/partner-analytics-metrics/order-states/failures`,
  },

  alerts: {
    list: `/alerts`,
    byId: (alertId: string) => `/alerts/${alertId}`,
    read: (alertId: string) => `/alerts/${alertId}/read`,
    delete: (alertId: string) => `/alerts/${alertId}/delete`,
    readAll: `/alerts/read-all`,
    bulk: `/alerts/bulk`,
    unreadCount: `/alerts/unread/count`,
    acknowledge: (alertId: string) => `/alerts/${alertId}/acknowledge`,
    dismiss: (alertId: string) => `/alerts/${alertId}/dismiss`,
    settings: `/alerts/settings`,
    admin: {
      create: `/alerts/admin`,
      update: (alertId: string) => `/alerts/admin/${alertId}`,
      delete: (alertId: string) => `/alerts/admin/${alertId}`,
    },
  },

  // alerts: {
  //   list: `todo/alerts`,
  //   byId: (alertId: string) => `todo/alerts/${alertId}`,
  //   read: (alertId: string) => `todo/alerts/${alertId}/read`,
  //   delete: (alertId: string) => `todo/alerts/${alertId}/delete`,
  //   readAll: `todo/alerts/read-all`,
  //   bulk: `todo/alerts/bulk`,
  //   unreadCount: `/alerts/unread/count`,
  //   acknowledge: (alertId: string) => `todo/alerts/${alertId}/acknowledge`,
  //   dismiss: (alertId: string) => `todo/alerts/${alertId}/dismiss`,
  //   settings: `todo/alerts/settings`,
  //   admin: {
  //     create: `todo/alerts/admin`,
  //     update: (alertId: string) => `todo/alerts/admin/${alertId}`,
  //     delete: (alertId: string) => `todo/alerts/admin/${alertId}`,
  //   },
  // },
};
